import React from "react";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, MARKS, INLINES } from "@contentful/rich-text-types";
import { resolveColor, colors, font } from "utils/styles";
import renderItem from "utils/renderItem";
import Row from "react-bootstrap/Row";
import getKey from "utils/getKey";

import Content from "./Content";
import ImageCard from "./ImageCard";

export default function Title(props = {}) {
	const fields = props.title?.fields;

	const color = props?.color
		? resolveColor(props.color)
		: fields?.color
		? resolveColor(fields?.color)
		: colors.darkBlue;
	const elem = props.element || "h1";
	const classes = fields?.class || props.className || "";
	const lineHeight = props.lineHeight || fields?.lineHeight;
	const letterSpacing = fields?.letterSpacing;
	const fontSize = props?.fontSize ? props.fontSize : fields?.fontSize ? fields.fontSize + "px" : false;
	const linkColor =
		fields?.linkColor && resolveColor(fields?.linkColor) ? resolveColor(fields?.linkColor) : color.green;
	const columnWidth = fields?.columnWidth !== undefined ? fields?.columnWidth : 12;
	const maxWidth = fields?.maxWidth !== undefined ? fields?.maxWidth : "100%";
	const fontWeight = props.fontWeight;
	const textAlign = props.textAlign || fields?.textAlign;
	const textJustify = props.justify || fields?.justify || "start";
	const fontFamily = props.fontFamily || fields?.fontFamily;
	const decoration = props.decoration || fields?.decoration || "none";

	const colorLine = fields?.colorLine ? resolveColor(fields?.colorLine) : null;
	const lineSide = fields?.colorLineSide;
	const thickness = fields?.colorLineThickness;
	const colorLinePadding = fields?.colorLinePadding;
	const backgroundColor = resolveColor(props.backgroundColor || fields?.backgroundColor);
	const containerClass = props?.containerClass
		? props?.containerClass
		: fields?.containerClass
		? fields?.containerClass
		: "";

	return (
		<div className={`row g-0 justify-content-${textJustify} ${containerClass}`}>
			<div
				style={{
					//conditionally add border for style
					...(colorLine && {
						[`border${lineSide}`]: `${thickness}px solid ${colorLine}`,
						...(colorLinePadding && {
							[`padding${lineSide}`]: `${colorLinePadding}em`,
						}),
					}),
				}}
				className={`TitleComponent col-${columnWidth} align-items-${textJustify} d-flex flex-column ${classes}`}
			>
				{props.text && elem === "h1" && <h1 className={classes}>{props.text}</h1>}
				{props.text && elem === "h2" && <h2 className={classes}>{props.text}</h2>}
				{props.text && elem === "h3" && <h3 className={classes}>{props.text}</h3>}
				{props.text && elem === "h4" && <h4 className={classes}>{props.text}</h4>}
				{props.text && elem === "h5" && <h5 className={classes}>{props.text}</h5>}
				{props.text && elem === "h6" && <h6 className={classes}>{props.text}</h6>}
				{fields?.text &&
					// !props.text &&
					documentToReactComponents(fields?.text, {
						renderText: (text) =>
							text.split("\n").flatMap((text, i) => [i > 0 && <br key={getKey()} />, text]),

						renderNode: {
							[BLOCKS.PARAGRAPH]: (node, children) => (
								<h1 key={getKey()} className={classes}>
									{children}
								</h1>
							),
							[BLOCKS.HEADING_1]: (node, children) => (
								<h1 key={getKey()} className={classes}>
									{children}
								</h1>
							),
							[BLOCKS.HEADING_2]: (node, children) => (
								<h2 key={getKey()} className={classes}>
									{children}
								</h2>
							),
							[BLOCKS.HEADING_3]: (node, children) => (
								<h3 key={getKey()} className={classes}>
									{children}
								</h3>
							),
							[BLOCKS.HEADING_4]: (node, children) => (
								<h4 key={getKey()} className={classes}>
									{children}
								</h4>
							),
							[BLOCKS.HEADING_5]: (node, children) => (
								<h5 key={getKey()} className={classes}>
									{children}
								</h5>
							),
							[BLOCKS.HEADING_6]: (node, children) => (
								<h6 key={getKey()} className={classes}>
									{children}
								</h6>
							),

							[BLOCKS.EMBEDDED_ASSET]: (node, children) => {
								return (
									<img
										key={getKey()}
										// className={`${node?.data?.target?.fields ? node?.data?.target?.fields : " "} w-100`}
										className={`w-100`}
										src={node?.data?.target?.fields?.file?.url}
										alt={node?.data?.target?.fields?.description}
									/>
								);
							},
							[INLINES.HYPERLINK]: (node, children) => {
								return (
									<a key={getKey()} href={node.data.uri}>
										{children}
									</a>
								);
							},

							[INLINES.EMBEDDED_ENTRY]: (node, children) => {
								if (!node?.data?.target) {
									//some kind of contentful richtext editor bug giving empty embedded element blocks
									return <></>;
								}
								const type = node.data.target.sys.contentType.sys.id;
								if (type === "media") {
									return <ImageCard inline={true} key={getKey()} data={node?.data?.target} />;
								} else if (type === "title") {
									return <Title key={getKey()} title={node?.data?.target} />;
								} else if (type === "content") {
									const nestedContentClass = node.data.target.fields?.nestedContentClass || "";

									return (
										<span key={getKey()} className={nestedContentClass}>
											<Content content={node?.data?.target} />
										</span>
									);
								} else {
									return <span key={getKey()}>{renderItem(node.data.target)}</span>;
								}
							},
						},
						renderMark: {
							[MARKS.BOLD]: (text) => {
								return (
									<b className="boldColor" key={getKey()}>
										{text}
									</b>
								);
							},
						},
					})}
				<style jsx>{`
					.TitleComponent {
						background: ${backgroundColor ? backgroundColor : "transparent"};
					}
					h1,
					h2,
					h3,
					h4,
					h5,
					h6 {
						text-align: ${textAlign || "start"};
						color: ${color} !important;
						font-size: ${fontSize ? `${fontSize}` : "inherit"};
						line-height: ${lineHeight ? `${lineHeight}px !important` : "inherit"};
						letter-spacing: ${letterSpacing ? `${letterSpacing}px !important` : "0px !important"};
						margin-bottom: 20px;
						max-width: ${maxWidth}px;
						font-weight: ${fontWeight || "inherit"};
						text-decoration: ${decoration};
					}

					a {
						color: ${linkColor} !important;
						text-decoration: none;
					}

					.boldColor {
						color: ${fields?.boldTextColor
							? resolveColor(fields?.boldTextColor)
							: resolveColor(fields?.color)};
					}
					h1 {
						font-family: ${fontFamily || "Athelas"};
						font-weight: bold;
					}

					@media screen and (max-width: 768px) {
						h1 {
							font-size: ${"36px"};

							line-height: ${lineHeight ? `${lineHeight}px` : "40px"};
						}
					}

					@media screen and (min-width: 768px) {
						h1 {
							font-size: ${fontSize || "40px"};

							line-height: ${lineHeight ? `${lineHeight}px` : "40px"};
						}
					}

					@media screen and (min-width: 992px) {
						h1 {
							font-size: ${fontSize || "46px"};

							line-height: ${lineHeight ? `${lineHeight}px` : "50px"};
						}
					}

					@media screen and (min-width: 1400px) {
						h1 {
							font-size: ${fontSize || "56px"};

							line-height: ${lineHeight ? `${lineHeight}px` : "60px"};
						}
					}

					h2 {
						font-weight: bold;
						font-size: ${fontSize || "36px"};

						line-height: ${lineHeight ? `${lineHeight}px` : "42px"};
						font-family: ${fontFamily || "Athelas"};
					}

					@media screen and (max-width: 991px) {
						h2 {
							font-size: ${fontSize || "30px"};

							line-height: ${lineHeight ? `${lineHeight}px` : "38px"};
						}
					}

					h3 {
						font-weight: bold;
						font-size: ${fontSize || "28px"};
						line-height: ${lineHeight ? `${lineHeight}px` : "42px"};
						font-family: ${fontFamily || "Athelas"};
					}

					h4 {
						font-size: 22px;
						line-height: ${lineHeight ? `${lineHeight}px` : "32px"};
						font-family: ${fontFamily || "Open Sans"};
					}

					h5 {
						font-size: 20px;
						font-weight: bold;
						font-family: ${fontFamily || "Open Sans"};
						line-height: ${lineHeight ? `${lineHeight}px` : "25px"};
					}

					h6 {
						font-size: 17px;
						font-family: ${fontFamily || "Open Sans"};
						line-height: ${lineHeight ? `${lineHeight}px` : "23px"};
						padding: 0;
					}
				`}</style>
			</div>
		</div>
	);
}
